// @claude-owned: LocalFabContext — sprint P0 v3 (loop fix)
import { createContext, useCallback, useContext, useId, useEffect, useMemo, useRef, useState, ReactNode } from 'react';

interface LocalFabCtx {
  hasLocalFab: boolean;
  register: (id: string) => void;
  unregister: (id: string) => void;
}

const Ctx = createContext<LocalFabCtx>({
  hasLocalFab: false,
  register: () => {},
  unregister: () => {},
});

export function LocalFabProvider({ children }: { children: ReactNode }) {
  const ownersRef = useRef<Set<string>>(new Set());
  const [hasLocalFab, setHasLocalFab] = useState(false);

  const register = useCallback((id: string) => {
    const set = ownersRef.current;
    if (set.has(id)) return;
    set.add(id);
    setHasLocalFab(set.size > 0);
  }, []);

  const unregister = useCallback((id: string) => {
    const set = ownersRef.current;
    if (!set.delete(id)) return;
    setHasLocalFab(set.size > 0);
  }, []);

  const value = useMemo<LocalFabCtx>(
    () => ({ hasLocalFab, register, unregister }),
    [hasLocalFab, register, unregister],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Pages with a local FAB call useLocalFab(true) on mount. */
export function useLocalFab(active: boolean = true) {
  const { register, unregister } = useContext(Ctx);
  const id = useId();
  useEffect(() => {
    if (!active) return;
    register(id);
    return () => unregister(id);
  }, [active, id, register, unregister]);
}

export function useHasLocalFab() {
  return useContext(Ctx).hasLocalFab;
}